import { AppShell, PageHeader } from '../components/AppShell'
import { Panel } from '../components/ui'
import { usePortal } from '../lib/portal-context'
import { Film, Link2, Mail, Upload } from 'lucide-react'

const FLOW = [
  {
    icon: Upload,
    title: 'Upload the evidence',
    body: 'Export the clip from your dashcam, doorbell, or phone and drop it in with any still photos. Files are stored unmodified and checksummed at upload.',
  },
  {
    icon: Film,
    title: 'Flag the key moments',
    body: 'Scrub to the moment the vehicle blows the stop sign and mark it. Markers appear on the report timeline so the office manager can jump straight there.',
  },
  {
    icon: Link2,
    title: 'Generate the case link',
    body: 'Submitting assigns a permanent case number and URL. The link opens the full report with playable video for anyone you send it to — no login.',
  },
  {
    icon: Mail,
    title: 'Send it to management',
    body: 'The portal writes the email for you, with the case link, the violation summary, the plate, and what you are asking the Association to do.',
  },
]

const SHORTCUTS = [
  ['Space or K', 'Play / pause'],
  ['← / →', 'Back / forward 1 second'],
  ['Shift + ← / →', 'Back / forward 10 seconds'],
  [', and .', 'Step one frame back / forward'],
  ['M', 'Mute'],
  ['F', 'Fullscreen'],
]

export function HelpPage() {
  const { config } = usePortal()

  return (
    <AppShell>
      <div className="mx-auto max-w-[900px] px-4 py-7 sm:px-6">
        <PageHeader
          eyebrow="How it works"
          title="From dashcam clip to HOA case file"
          blurb={`This portal exists because ${
            config?.community ?? 'Altair Irvine'
          } is a private community: Irvine PD does not routinely patrol these streets, so unsafe driving only gets addressed when the Association receives documented reports it can act on.`}
        />

        <div className="mb-6 grid gap-4 sm:grid-cols-2">
          {FLOW.map(({ icon: Icon, title, body }, index) => (
            <div key={title} className="panel px-4 py-4">
              <div className="flex items-center gap-2.5">
                <span className="flex h-8 w-8 items-center justify-center rounded-lg border border-altair-500/30 bg-altair-500/10 text-altair-400">
                  <Icon size={16} />
                </span>
                <span className="text-[11px] font-bold uppercase tracking-[0.14em] text-slate-500">
                  Step {index + 1}
                </span>
              </div>
              <h3 className="mt-3 text-[15px] font-semibold text-white">{title}</h3>
              <p className="mt-1.5 text-[13px] leading-relaxed text-slate-400">{body}</p>
            </div>
          ))}
        </div>

        <div className="grid gap-5 sm:grid-cols-2">
          <Panel title="Video review shortcuts">
            <dl className="space-y-2">
              {SHORTCUTS.map(([keys, action]) => (
                <div key={keys} className="flex items-center justify-between gap-3">
                  <dt className="kbd">{keys}</dt>
                  <dd className="text-[12.5px] text-slate-400">{action}</dd>
                </div>
              ))}
            </dl>
          </Panel>

          <Panel title="What makes a report stick">
            <ul className="space-y-2 text-[13px] leading-relaxed text-slate-300">
              <li>A legible plate is the single most valuable detail — freeze-frame it if needed.</li>
              <li>Note whether children or pedestrians were present. It changes how the board reacts.</li>
              <li>Keep the narrative factual and chronological; skip speculation about the driver.</li>
              <li>Log repeat offenders. A pattern across dates is what justifies a fine over a warning.</li>
              <li>Submit promptly — dashcam footage overwrites itself on a loop.</li>
            </ul>
          </Panel>
        </div>

        <Panel title="Getting a dashcam clip off the car" className="mt-5">
          <ol className="list-decimal space-y-1.5 pl-5 text-[13px] leading-relaxed text-slate-300">
            <li>Save or export the clip from the camera (dashcam, doorbell, or phone) while it is still on the device.</li>
            <li>Copy the original .mp4 / .mov file onto this computer — not a screenshot or Photos thumbnail.</li>
            <li>
              Tesla USB drives: look in <code className="rounded bg-black/40 px-1">TeslaCam/SavedClips</code> or{' '}
              <code className="rounded bg-black/40 px-1">SentryClips</code>, in the folder matching the
              timestamp. The front camera file is usually the useful one.
            </li>
            <li>
              Drop the file into a new report here. Trimming is unnecessary — key-moment markers point
              the reviewer to the right second.
            </li>
          </ol>
        </Panel>
      </div>
    </AppShell>
  )
}
